/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { createContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Tooltip,
  Tab,
} from '@material-ui/core';
import {
  Panel,
  PreventTransitionPrompt,
  useDialogs,
  TextField,
  Tabs,
  AppBar,
} from '@michelin/acid-components';
import { HelpOutline } from '@material-ui/icons';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { saveCommercialMechanicalServices, useCommercialMechanicalServices } from 'hooks/useCommercialServices';
import { getOptionLabel, getOptions } from '@michelin/select-options-provider';
import { useSnackbar } from 'notistack';
import { Contact } from 'Types';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { GenericActionButtonsMassUpdate } from 'components/GenericActionButtons/GenericActionButtonsMassUpdate';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { scrollToDisplayFirstError } from 'Utils';
import { CommercialMechanicalServices, commercialMechanicalServicesLabels, commercialMechanicalServicesLabelsAlt } from './CommercialServicesFields';
import { CheckboxGroup } from './CheckboxGroup';
import { FullChecboxPanel } from './FullCheckboxTools';
import ModalHelp from './ModalHelp';
import MultiSelectTool from './MultiSelectTool';
import CopyServices from '../CopyServices/CopyServices';

interface Context {
  certificateServiceTypes: Certificates | undefined,
  certificateServiceTypesErrors: CertificatesErrors | undefined,
  handleAssignContact: (contact: Contact, serviceType: string, serviceTypeLabel: string) => void,
  handleAssignContactError: (field: string, value: boolean | undefined) => void,
}

const contextInit: Context = {
  certificateServiceTypes: {},
  certificateServiceTypesErrors: {},
  handleAssignContact: () => { },
  handleAssignContactError: () => { },
};

export const ContextAssignContact: React.Context<Context> = createContext(contextInit);

export interface Certificates {
  [key: string]: boolean;
}

interface CertificatesErrors {
  [key: string]: boolean | undefined;
}

interface Props {
  services: CommercialMechanicalServices,
  certificates?: Certificates,
  action: 'edit' | 'view' | 'add',
  fromMassUpdate?: boolean,
}

function CommercialServicesPanel(props: Props) {
  const { t } = useTranslation();
  const permissions = usePermissionsSPP();
  const { stNumber } = useParams<{ stNumber: string }>();
  const history = useHistory();
  const { errorDialog } = useDialogs();
  const { enqueueSnackbar } = useSnackbar();

  const {
    formState, getValues, reset, control, setValue, watch, handleSubmit,
  } = useForm({ defaultValues: props.services });
  const queryClient = useQueryClient();
  const saveMutation = useMutation(saveCommercialMechanicalServices, {
    onSuccess: () => queryClient.invalidateQueries(['commercial-mechanical-services', stNumber]),
  });

  const readOnly: boolean = props.action === 'view';
  const isDirty = Object.keys(formState.dirtyFields).length > 0;
  const [selectedTab, setSelectedTab] = useState('main');
  const countryCode = (permissions.location?.customer_country || 'us').toLocaleLowerCase();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'level1' | 'level2' | 'level3' | 'level4' | 'level5'>('level1');

  const heavyTractorList = getOptions('spp_authorized_service_provider_heavy_duty_tractor');
  const heavyTrailerList = getOptions('spp_authorized_service_provider_heavy_duty_trailer');
  const mediumTrailerList = getOptions('spp_authorized_service_provider_medium_duty_trailer');

  const [certificateServiceTypes, setCertificateServiceTypes] = useState<Certificates | undefined>(undefined);
  const [certificateServiceTypesErrors, setCertificateServiceTypesErrors] = useState<CertificatesErrors | undefined>(undefined);

  const [hasInBay, setHasInBay] = useState<boolean>(false);
  const [hasMobile, setHasMobile] = useState<boolean>(false);

  useEffect(() => {
    if (props.certificates) {
      setCertificateServiceTypes(props.certificates);
      let ce: CertificatesErrors | undefined;
      Object.entries(props.certificates).forEach(([key, value]) => {
        ce = {
          ...ce,
          [key]: value ? false : undefined,
        };
      });
      setCertificateServiceTypesErrors(ce);
    }
  }, [props.certificates]);

  useEffect(() => {
    if (props.action === 'view') reset();
  }, [props.action, reset]);

  useEffect(() => {
    reset(props.services);
  }, [props.services, reset]);

  const onError = () => {
    setSelectedTab('level5');
    scrollToDisplayFirstError();

    errorDialog(
      t('Please check the marked fields.').split('\n'),
      t('Error saving commercial data.'),
    );
  };

  const save = async () => {
    // validate certificates
    const certErrors: string[] = [];
    if (certificateServiceTypes && certificateServiceTypesErrors && countryCode === 'us') {
      Object.entries(certificateServiceTypes).forEach(([key, value]) => {
        if (value === false) {
          if (certificateServiceTypesErrors[key] === true) {
            certErrors.push(getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', key));
          } else if (formState.isDirty) {
            const serviceDirtyAtLevel1 = JSON.stringify(formState.dirtyFields.basic_mechanical || '').includes(`"${key}`);
            const serviceDirtyAtLevel2 = JSON.stringify(formState.dirtyFields.light_mechanical || '').includes(`"${key}`);
            const serviceCheckedAtLevel1 = JSON.stringify((getValues().basic_mechanical as any)[key] || '').includes(':true');
            const serviceCheckedAtLevel2 = JSON.stringify((getValues().light_mechanical as any)[key] || '').includes(':true');
            if ((serviceDirtyAtLevel1 || serviceDirtyAtLevel2) && (serviceCheckedAtLevel1 || serviceCheckedAtLevel2)) {
              certErrors.push(getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', key));
              setCertificateServiceTypesErrors({ ...certificateServiceTypesErrors, [key]: true });
            }
          }
        }
      });

      if (certErrors.length > 0) {
        errorDialog(
          certErrors,
          t('Missing Required Certificate(s)'),
          t('Ok'),
        );
        return;
      }
    }

    try {
      if (!isDirty) return;
      const data = getValues();
      if (countryCode === 'ca') delete data.ase_certified_mechanics;
      await saveMutation.mutateAsync({ stNumber, data });
      reset(getValues());
      history.push('view');
      enqueueSnackbar(t('Commercial Mechanical Services information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (error) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Commercial Products Data.'),
      );
    }
  };

  let checkInBay: boolean = false;
  let checkMobile: boolean = false;

  const isLevelEnebled = (level: 'level4' | 'level5') => {
    const form = getValues();

    const level1Selected = JSON.stringify(form.basic_mechanical || '').includes(':true');
    const level2Selected = JSON.stringify(form.light_mechanical || '').includes(':true');
    const level3Selected = JSON.stringify(form.wet_mechanical || '').includes(':true');
    const level4Selected = JSON.stringify(form.full_mechanical || '').includes(':true');

    switch (level) {
      case 'level4':
        // eslint-disable-next-line no-case-declarations
        const levelSelected: boolean = level1Selected || level2Selected || level3Selected;

        if (levelSelected) {
          const mechServicesTabs: string[] = ['basic_mechanical', 'light_mechanical', 'wet_mechanical'];
          mechServicesTabs.forEach((tab: string) => {
            if ((form as any)[tab]) {
              Object.entries((form as any)[tab]).forEach(([k]) => {
                Object.entries(((form as any)[tab] as any)[k]).forEach(([k1]) => {
                  if (tab === 'wet_mechanical') {
                    Object.entries(((form as any)[tab] as any)[k][k1]).forEach(([k2]) => {
                      if (k2 === 'in_bay' && JSON.stringify(((form as any)[tab] as any)[k][k1][k2] || '').includes(':true')) checkInBay = true;
                      if (k2 === 'mobile' && JSON.stringify(((form as any)[tab] as any)[k][k1][k2] || '').includes(':true')) checkMobile = true;
                    });
                  } else {
                    if (k1 === 'in_bay' && JSON.stringify(((form as any)[tab] as any)[k][k1] || '').includes(':true')) checkInBay = true;
                    if (k1 === 'mobile' && JSON.stringify(((form as any)[tab] as any)[k][k1] || '').includes(':true')) checkMobile = true;
                  }
                });
              });
            }
          });
        }

        return levelSelected;
      // return level1Selected || level2Selected || level3Selected;
      case 'level5':
        return level1Selected || level2Selected || level3Selected || level4Selected;
      default: return false;
    }
  };

  const checkAuthorizedService = (list: string[]) => {
    if (!list || list.length === 0) return true;
    let error: boolean = false;

    list.forEach((item, index) => {
      if (error) return;

      const cloneList = [...list];
      cloneList.splice(index, 1);
      error = cloneList.includes(item) || item === 'other:' || item === 'other';
    });

    return !error;
  };

  const level4Enabled = isLevelEnebled('level4');
  const level5Enabled = isLevelEnebled('level5');

  useEffect(() => {
    // Reset the values from Level 4 && 5 when are disabled.
    if (!level4Enabled && !level5Enabled) {
      const level4 = JSON.stringify(getValues().full_mechanical || '');
      const level5 = getValues().complete_mechanical;

      // Check if the values from main are dirty to set the correct state of the object.
      const shouldDirty = JSON.stringify(getValues() || '').includes(':true') || getValues().number_of_dedicated_mobile_mechanical_units !== null;

      // Clean the values from Level 4
      if (level4 !== '""') setValue('full_mechanical', JSON.parse(level4.replaceAll(':true', ':false')), { shouldDirty });

      // Clean the arrays of Level 5
      if (level5) {
        if (level5?.authorized_service_of?.heavy_duty_tractor_oe_manufacturers) level5.authorized_service_of.heavy_duty_tractor_oe_manufacturers.length = 0;
        if (level5?.authorized_service_of?.heavy_duty_trailer_oe_manufacturers) level5.authorized_service_of.heavy_duty_trailer_oe_manufacturers.length = 0;
        if (level5?.authorized_service_of?.medium_duty_oe_manufacturers) level5.authorized_service_of.medium_duty_oe_manufacturers.length = 0;

        setValue('complete_mechanical', level5, { shouldDirty });
      }
    } else if (level4Enabled) {
      setHasInBay(checkInBay);
      setHasMobile(checkMobile);
    }
  }, [level4Enabled, level5Enabled, checkInBay, checkMobile, getValues, setValue]);

  const handleAssignContact = (contact: Contact, serviceType: string, serviceTypeLabel: string) => {
    const cloneCertificate = { ...certificateServiceTypes };
    cloneCertificate[serviceType] = true;
    setCertificateServiceTypes(cloneCertificate);

    enqueueSnackbar(t('Contact {{contact_name}} assigned to {{service_type}}.', {
      contact_name: `${contact.first_name} ${contact.last_name}`,
      service_type: t(serviceTypeLabel),
    }), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
  };

  const handleAssignContactError = (field: string, value: boolean | undefined) => {
    if (field === 'select_all' && certificateServiceTypes && countryCode !== 'ca') { // canada does not rquire certificates
      if (selectedTab !== 'level1' && selectedTab !== 'level2') return; // certs only used in level 1 & level 2
      let certErrs: CertificatesErrors = {};
      Object.keys(certificateServiceTypes).forEach((key: string) => {
        certErrs = {
          ...certErrs,
          [key]: value,
        };
      });
      if (selectedTab === 'level1') delete certErrs.state_inspection;
      setCertificateServiceTypesErrors({
        ...certErrs,
      });
    } else {
      setCertificateServiceTypesErrors({
        ...certificateServiceTypesErrors,
        [field]: value,
      });
    }
  };

  return (
    <>
      <ContextAssignContact.Provider
        value={{
          certificateServiceTypes,
          certificateServiceTypesErrors,
          handleAssignContact,
          handleAssignContactError,
        }}
      >
        <AppBar style={{ height: 48 }} color="primary" position="static">
          <Tabs
            value={selectedTab}
            onChange={(event, newValue: string) => {
              if (newValue === 'level4' && !level4Enabled) return;
              if (newValue === 'level5' && !level5Enabled) return;

              setSelectedTab(newValue);
            }}
            variant="scrollable"
            scrollButtons="on"
          >
            <Tab label={t('Main')} value="main" style={{ fontWeight: 'bold' }} />
            <Tab label={t('Level I - Trailer')} value="level1" style={{ fontWeight: 'bold' }} />
            <Tab label={t('Level II - Power Unit')} value="level2" style={{ fontWeight: 'bold' }} />
            <Tab label={t('Level III - Fluids')} value="level3" style={{ fontWeight: 'bold' }} />
            <Tab
              label={(
                <Tooltip placement="top" arrow title={(level4Enabled ? '' : t('Select at least one service from Level 1, Level 2 or Level 3.')) || ''}>
                  <span>{t('Level IV - Full')}</span>
                </Tooltip>
              )}
              value="level4"
              style={{ opacity: (level4Enabled ? 0.7 : 0.5), fontWeight: 'bold' }}
            />
            <Tab
              label={(
                <Tooltip placement="top" arrow title={(level5Enabled ? '' : t('Select at least one service from Level 1, Level 2, Level 3 or Level 4.')) || ''}>
                  <span>{t('Level V - Complete')}</span>
                </Tooltip>
              )}
              value="level5"
              style={{ opacity: (level5Enabled ? 0.7 : 0.5), fontWeight: 'bold' }}
            />
          </Tabs>
        </AppBar>
        <ModalHelp type={type} open={open} onClose={() => setOpen(false)} />
        <Panel>
          {(props.fromMassUpdate || false) ? (
            <GenericActionButtonsMassUpdate
              isDirty={isDirty}
              getValues={getValues}
              reset={reset}
              handleSubmit={handleSubmit}
              validateAction="mass_st_update.update"
            />
          )
            : (
              <GenericActionButtons
                isDirty={isDirty}
                isSaving={saveMutation.isLoading}
                onSave={handleSubmit(save, onError)}
                validateAction="location_information.update"
                readOnly={readOnly}
                additionalEditButtons={[
                  <CopyServices
                    key="copyServices"
                    getServicesHook={useCommercialMechanicalServices}
                    setValue={setValue}
                    type="mechanical"
                  />]}
              />
            )}

          <TabPanel index="main" value={selectedTab}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <CheckboxGroup
                  getValues={getValues}
                  title={t('Towing')}
                  disableGroupSelector
                  control={control}
                  cols={4}
                  labels={(countryCode === 'us' ? commercialMechanicalServicesLabels : commercialMechanicalServicesLabelsAlt)}
                  readOnly={readOnly}
                  setValue={setValue}
                  watch={watch}
                  items={[
                    { key: 'towing.less_than_10_lbs' },
                    { key: 'towing.between_10_and_26_lbs' },
                    { key: 'towing.greater_than_26_lbs' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Panel title={t('Dedicated Mobile Units')}>
                  <Controller
                    name="number_of_dedicated_mobile_mechanical_units"
                    control={control}
                    render={(item) => (
                      <TextField
                        label={t('# of Dedicated Mobile Mechanical Units')}
                        readOnly={readOnly}
                        type="number"
                        value={item.field.value || ''}
                        onChange={(newValue) => item.field.onChange(Math.max(0, Math.min(100000000, parseInt(newValue.target.value.toString(), 10))))}
                        inputProps={{
                          min: 0,
                          decimals: 0,
                          step: 1,
                          readOnly,
                        }}
                      />
                    )}
                  />
                </Panel>
              </Grid>
              {countryCode === 'us' && (
                <Grid item xs={12}>
                  <CheckboxGroup
                    getValues={getValues}
                    title={t('ASE Certified Mechanics')}
                    control={control}
                    cols={6}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    items={[
                      { key: 'ase_certified_mechanics.a1_engine_repair' },
                      { key: 'ase_certified_mechanics.a2_automatic_transmission_transaxle' },
                      { key: 'ase_certified_mechanics.a3_manual_drive_train_and_axles' },
                      { key: 'ase_certified_mechanics.a4_suspension_and_steering' },
                      { key: 'ase_certified_mechanics.a5_brakes' },
                      { key: 'ase_certified_mechanics.a6_electrical_electronic_systems' },
                      { key: 'ase_certified_mechanics.a7_heating_and_air_conditioning' },
                      { key: 'ase_certified_mechanics.a8_engine_performance' },
                      { key: 'ase_certified_mechanics.a9_light_vehicles_diesel_engines' },
                      { key: 'ase_certified_mechanics.g1_auto_maintenance_and_light_repair' },
                      { key: 'ase_certified_mechanics.l3_light_duty_hybrid_electric_vehicle' },
                    ]}
                  />
                </Grid>
              )}
            </Grid>
          </TabPanel>

          <TabPanel index="level1" value={selectedTab}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Panel
                  title={t('Level I - Basic Mechanical - Trailer')}
                  icon={(
                    <HelpOutline
                      onClick={() => {
                        setType('level1');
                        setOpen(!open);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                >
                  <FullChecboxPanel
                    getValues={getValues}
                    control={control}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    groups={[
                      {
                        label: t((countryCode === 'us' ? 'Trailers < 10,000 lbs' : 'Trailers < 4,536 kg')),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.trailers_less_than_10000_lbs' },
                          { label: t('Mobile'), path: 'mobile.trailers_less_than_10000_lbs' },
                        ],
                      },
                      {
                        label: t((countryCode === 'us' ? 'Trailers >= 10,000 lbs' : 'Trailers >= 4,536 kg')),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.trailers_greater_than_10000_lbs' },
                          { label: t('Mobile'), path: 'mobile.trailers_greater_than_10000_lbs' },
                        ],
                      },
                    ]}
                    items={[
                      { key: 'basic_mechanical.alignment' },
                      ...(countryCode === 'ca' ? [{
                        key: 'basic_mechanical.basic_brake_service',
                      }] : [{
                        key: 'basic_mechanical.basic_brake_service',
                        notes: t('(Mechanic Certificates must be on file. Please update in Contacts)'),
                      }]),
                      { key: 'basic_mechanical.basic_electrical' },
                      ...(countryCode === 'us' ? [{
                        key: 'basic_mechanical.dot_annual_inspection',
                        notes: t('(Mechanic Certificates must be on file. Please update in Contacts)'),
                      }] : [] as any[]),
                      { key: 'basic_mechanical.light_replacement' },
                      { key: 'basic_mechanical.minor_body_repair' },
                      ...(countryCode === 'ca' ? [{ key: 'basic_mechanical.pmvi' }] : [] as any[]),
                      { key: 'basic_mechanical.seals_and_bearing_replacement' },
                      { key: 'basic_mechanical.trailer_inspection' },
                      { key: 'basic_mechanical.vehicle_disinfecting' },
                      { key: 'basic_mechanical.vehicle_washes' },
                      { key: 'basic_mechanical.wheel_end_replacement' },
                    ]}
                  />
                </Panel>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel index="level2" value={selectedTab}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Panel
                  title={t('Level II - Light Mechanical - Power Unit')}
                  icon={(
                    <HelpOutline
                      onClick={() => {
                        setType('level2');
                        setOpen(!open);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                >
                  <FullChecboxPanel
                    getValues={getValues}
                    control={control}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    groups={[
                      {
                        label: t('Lt/Md Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.light_medium_duty' },
                          { label: t('Mobile'), path: 'mobile.light_medium_duty' },
                        ],
                      },
                      {
                        label: t('Heavy Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.heavy_duty' },
                          { label: t('Mobile'), path: 'mobile.heavy_duty' },
                        ],
                      },
                    ]}
                    items={[
                      { key: 'light_mechanical.air_conditioning_repair' },
                      { key: 'light_mechanical.air_filter_replacement' },
                      { key: 'light_mechanical.alignment' },
                      { key: 'light_mechanical.alternator_replacement' },
                      ...(countryCode === 'ca' ? [{
                        key: 'light_mechanical.basic_brake_service',
                      }] : [{
                        key: 'light_mechanical.basic_brake_service',
                        notes: t('(Mechanic Certificates must be on file. Please update in Contacts)'),
                      }]),
                      { key: 'light_mechanical.basic_electrical' },
                      { key: 'light_mechanical.battery_replacement' },
                      { key: 'light_mechanical.belts_and_hoses' },
                      { key: 'light_mechanical.cabin_filter_replacement' },
                      ...(countryCode === 'us' ? [{
                        key: 'light_mechanical.dot_annual_inspection',
                        notes: t('(Mechanic Certificates must be on file. Please update in Contacts)'),
                      }] : [] as any[]),
                      { key: 'light_mechanical.electric_vehicle_charging' },
                      { key: 'light_mechanical.full_unit_inspection' },
                      { key: 'light_mechanical.light_replacement' },
                      { key: 'light_mechanical.lightbulb_replacement' },
                      ...(countryCode === 'ca' ? [{ key: 'light_mechanical.pmvi' }] : [] as any[]),
                      ...(countryCode === 'us' ? [{
                        key: 'light_mechanical.state_inspection',
                        notes: t('(Mechanic Certificates must be on file. Please update in Contacts)'),
                      }] : [] as any[]),
                      { key: 'light_mechanical.seals_and_bearing_replacement' },
                      { key: 'light_mechanical.suspension' },
                      { key: 'light_mechanical.vehicle_disinfecting' },
                      { key: 'light_mechanical.vehicle_washes' },
                      { key: 'light_mechanical.wheel_end_replacement' },
                      { key: 'light_mechanical.windshield_wipers' },
                    ]}
                  />
                </Panel>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel index="level3" value={selectedTab}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Panel
                  title={t('Refills/Top Offs')}
                  icon={(
                    <HelpOutline
                      onClick={() => {
                        setType('level3');
                        setOpen(!open);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                >
                  <FullChecboxPanel
                    getValues={getValues}
                    control={control}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    groups={[
                      {
                        label: t('Lt/Md Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.light_medium_duty' },
                          { label: t('Mobile'), path: 'mobile.light_medium_duty' },
                        ],
                      },
                      {
                        label: t('Heavy Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.heavy_duty' },
                          { label: t('Mobile'), path: 'mobile.heavy_duty' },
                        ],
                      },
                    ]}
                    items={[
                      { key: 'wet_mechanical.refills_top_offs.brake_fluids' },
                      { key: 'wet_mechanical.refills_top_offs.coolant' },
                      { key: 'wet_mechanical.refills_top_offs.diesel_emission_fluid' },
                      { key: 'wet_mechanical.refills_top_offs.fuel_refill_diesel' },
                      { key: 'wet_mechanical.refills_top_offs.fuel_refill_gas' },
                      { key: 'wet_mechanical.refills_top_offs.oil' },
                      { key: 'wet_mechanical.refills_top_offs.power_steering_fluid' },
                      { key: 'wet_mechanical.refills_top_offs.transmission_fluid' },
                      { key: 'wet_mechanical.refills_top_offs.windshield_fluid' },
                    ]}
                  />
                </Panel>
              </Grid>
              <Grid item xs={12}>
                <Panel
                  title={t('Flushes')}
                  icon={(
                    <HelpOutline
                      onClick={() => {
                        setType('level3');
                        setOpen(!open);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                >
                  <FullChecboxPanel
                    getValues={getValues}
                    control={control}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    groups={[
                      {
                        label: t('Lt/Md Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.light_medium_duty' },
                          { label: t('Mobile'), path: 'mobile.light_medium_duty' },
                        ],
                      },
                      {
                        label: t('Heavy Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.heavy_duty' },
                          { label: t('Mobile'), path: 'mobile.heavy_duty' },
                        ],
                      },
                    ]}
                    items={[
                      { key: 'wet_mechanical.flushes.brake_fluid' },
                      { key: 'wet_mechanical.flushes.coolant' },
                      { key: 'wet_mechanical.flushes.differential' },
                      { key: 'wet_mechanical.flushes.fuel_system_cleaning' },
                      { key: 'wet_mechanical.flushes.oil' },
                      { key: 'wet_mechanical.flushes.power_steering_fluid' },
                      { key: 'wet_mechanical.flushes.transmission' },
                    ]}
                  />
                </Panel>
              </Grid>
              <Grid item xs={12}>
                <Panel
                  title={t('Other Services')}
                  icon={(
                    <HelpOutline
                      onClick={() => {
                        setType('level3');
                        setOpen(!open);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                >
                  <FullChecboxPanel
                    getValues={getValues}
                    control={control}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    groups={[
                      {
                        label: t('Lt/Md Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.light_medium_duty' },
                          { label: t('Mobile'), path: 'mobile.light_medium_duty' },
                        ],
                      },
                      {
                        label: t('Heavy Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.heavy_duty' },
                          { label: t('Mobile'), path: 'mobile.heavy_duty' },
                        ],
                      },
                    ]}
                    items={[
                      { key: 'wet_mechanical.other_services.brake_fluid_exchange' },
                      { key: 'wet_mechanical.other_services.coolant_exchange' },
                      { key: 'wet_mechanical.other_services.differential_fluid_exchange' },
                      { key: 'wet_mechanical.other_services.fuel_system_repair' },
                      { key: 'wet_mechanical.other_services.oil_change' },
                      { key: 'wet_mechanical.other_services.power_steering_fluid_exchange' },
                      { key: 'wet_mechanical.other_services.transfer_case_fluid_exchange' },
                      { key: 'wet_mechanical.other_services.transmission_fluid_exchange' },
                    ]}
                  />
                </Panel>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel index="level4" value={selectedTab}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Panel
                  title={t('Level IV - Full Mechanical')}
                  icon={(
                    <HelpOutline
                      onClick={() => {
                        setType('level4');
                        setOpen(!open);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                >
                  <FullChecboxPanel
                    getValues={getValues}
                    control={control}
                    labels={commercialMechanicalServicesLabels}
                    readOnly={readOnly}
                    setValue={setValue}
                    watch={watch}
                    groups={[
                      {
                        label: t('Lt/Md Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.light_medium_duty' },
                          { label: t('Mobile'), path: 'mobile.light_medium_duty' },
                        ],
                      },
                      {
                        label: t('Heavy Duty'),
                        cols: [
                          { label: t('In Bay'), path: 'in_bay.heavy_duty' },
                          { label: t('Mobile'), path: 'mobile.heavy_duty' },
                        ],
                      },
                    ]}
                    items={[
                      {
                        key: 'full_mechanical.electrical_diagnostics',
                        ...(hasInBay && !hasMobile ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty'] } : {}),
                        ...(hasMobile && !hasInBay ? { enabledPaths: ['mobile.light_medium_duty', 'mobile.heavy_duty'] } : {}),
                        ...(hasInBay && hasMobile ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty', 'mobile.light_medium_duty', 'mobile.heavy_duty'] } : {}),
                      },
                      {
                        key: 'full_mechanical.engine_diagnostics.basic',
                        ...(hasInBay && !hasMobile ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty'] } : {}),
                        ...(hasMobile && !hasInBay ? { enabledPaths: ['mobile.light_medium_duty', 'mobile.heavy_duty'] } : {}),
                        ...(hasInBay && hasMobile ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty', 'mobile.light_medium_duty', 'mobile.heavy_duty'] } : {}),
                      },
                      {
                        key: 'full_mechanical.engine_diagnostics.advanced',
                        ...(hasInBay ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty'] } : { enabledPaths: [] }),
                      },
                      {
                        key: 'full_mechanical.transmission.basic',
                        ...(hasInBay && !hasMobile ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty'] } : {}),
                        ...(hasMobile && !hasInBay ? { enabledPaths: ['mobile.light_medium_duty', 'mobile.heavy_duty'] } : {}),
                        ...(hasInBay && hasMobile ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty', 'mobile.light_medium_duty', 'mobile.heavy_duty'] } : {}),
                      },
                      {
                        key: 'full_mechanical.transmission.advanced',
                        ...(hasInBay ? { enabledPaths: ['in_bay.light_medium_duty', 'in_bay.heavy_duty'] } : { enabledPaths: [] }),
                      },
                    ]}
                  />
                </Panel>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel index="level5" value={selectedTab}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Panel title={t('Level V - Complete Mechanical Services - OE Manufactures')}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Panel title={t('Medium Duty')}>
                        <Controller
                          name="complete_mechanical.authorized_service_of.medium_duty_oe_manufacturers"
                          control={control}
                          rules={{ validate: (value: string[]) => checkAuthorizedService(value) }}
                          render={(item) => (
                            <MultiSelectTool
                              name="medium_duty_oe_manufacturers"
                              emptyLabel={t('No authorized service providers')}
                              selectLabel={t('Authorized service provider')}
                              textLabel={t('Other description')}
                              data={mediumTrailerList}
                              list={item.field.value || []}
                              readOnly={readOnly}
                              onChange={(newData: string[]) => {
                                item.field.onChange(newData);
                              }}
                            />
                          )}
                        />
                      </Panel>
                    </Grid>
                    <Grid item xs={4}>
                      <Panel title={t('Heavy Duty Trailer')}>
                        <Controller
                          name="complete_mechanical.authorized_service_of.heavy_duty_trailer_oe_manufacturers"
                          control={control}
                          rules={{ validate: (value: string[]) => checkAuthorizedService(value) }}
                          render={(item) => (
                            <MultiSelectTool
                              name="heavy_duty_trailer_oe_manufacturers"
                              emptyLabel={t('No authorized service providers')}
                              selectLabel={t('Authorized service provider')}
                              textLabel={t('Other description')}
                              data={heavyTrailerList}
                              list={item.field.value || []}
                              readOnly={readOnly}
                              onChange={(newData: string[]) => {
                                item.field.onChange(newData);
                              }}
                            />
                          )}
                        />
                      </Panel>
                    </Grid>
                    <Grid item xs={4}>
                      <Panel title={t('Heavy Duty Tractor')}>
                        <Controller
                          name="complete_mechanical.authorized_service_of.heavy_duty_tractor_oe_manufacturers"
                          control={control}
                          rules={{ validate: (value: string[]) => checkAuthorizedService(value) }}
                          render={(item) => (
                            <MultiSelectTool
                              name="heavy_duty_tractor_oe_manufacturers"
                              emptyLabel={t('No authorized service providers')}
                              selectLabel={t('Authorized service provider')}
                              textLabel={t('Other description')}
                              data={heavyTractorList}
                              list={item.field.value || []}
                              readOnly={readOnly}
                              onChange={(newData: string[]) => {
                                item.field.onChange(newData);
                              }}
                            />
                          )}
                        />
                      </Panel>
                    </Grid>
                  </Grid>
                </Panel>
              </Grid>
            </Grid>
          </TabPanel>
        </Panel>
        <PreventTransitionPrompt
          when={isDirty}
        />
      </ContextAssignContact.Provider>
    </>
  );
}

export default CommercialServicesPanel;

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function TabPanel(props: TabPanelProps): JSX.Element | null {
  if (props.index !== props.value) return null;
  return <span>{props.children}</span>;
}
